/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Reconcile Payment Button
 */
import { useState } from 'react';
import { Button, ButtonProps, Descriptions, Flex, Input, Modal, Tag } from 'antd';
import useUpdateERA from 'hooks/payments/mutate/useUpdateERA';
import useUpdateEntityNote from 'hooks/mutate/useUpdateEntityNote';
import { formatAmount } from 'utils/accounts/allAccounts';
import { getReconciledStatus } from 'utils/payment/era';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';
import { PAYMENT_RECONCILIATION_OUTCOME, PAYMENT_RECONCILIATION_STATUS } from 'app-constants/claims/era';
import { EMPTY_VALUE } from '@veritable/common/constants';

// comment label for the modal
const commentLabel = (
	<Flex gap={2}>
		<span style={{ fontWeight: 500, color: '#595959' }}>{'Add a comment'}</span>
		<span style={{ color: '#8c8c8c' }}>{'(optional)'}</span>
	</Flex>
);

export interface IProps extends ButtonProps {
	data: ERA.ERADetailsResponse | ERA.ERAListResponse;
}

/**
 * Reconcile Payment button
 * @param {IProps} props props
 * @returns {React.FC} Reconcile Payment
 */
const ReconcilePayment: React.FC<IProps> = ({ data, ...restProps }) => {
	const { showReconcileButton, showReconciledTag } = getReconciledStatus(data);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [note, setNote] = useState('');
	const { mutate: updateERA, isPending } = useUpdateERA();
	const { mutate: updateNote, isPending: isUpdatingNote } = useUpdateEntityNote();

	const fhirResource = data?.fhirResource;
	const loading = isPending || isUpdatingNote;

	const handleCancel = () => {
		setIsModalOpen(false);
		setNote('');
	};
	const handleOk = () => {
		updateERA(
			{
				requestParams: {
					paymentReconciliationId: data?.fhirResource?.id,
					eRAPatchModel: {
						meta: fhirResource?.meta,
						status: PAYMENT_RECONCILIATION_STATUS.active,
						outcome: PAYMENT_RECONCILIATION_OUTCOME.complete,
					},
				},
			},
			{
				onSuccess: () => {
					if (note) {
						updateNote(
							{
								entityType: fhirResource?.resourceType,
								entityId: fhirResource?.id,
								notePatchModel: { note, id: data?.userNote?.id },
							},
							{
								onSuccess: () => {
									handleCancel();
								},
							},
						);
					} else {
						handleCancel();
					}
				},
			},
		);
	};
	if (showReconcileButton) {
		return (
			<>
				<Button type='primary' onClick={() => setIsModalOpen(true)} {...restProps} data-testid='ipr-reconcile-btn'>
					{'Reconcile'}
				</Button>
				<Modal
					width={550}
					title={'Confirm Insurance Report (ERA) Payment'}
					destroyOnHidden
					open={isModalOpen}
					onCancel={handleCancel}
					onOk={handleOk}
					okText={'Confirm'}
					data-testid='ipr-reconcile-modal'
					okButtonProps={{ loading, 'data-testid': 'ipr-reconcile-confirm' }}
					cancelButtonProps={{ disabled: loading, 'data-testid': 'ipr-reconcile-cancel' }}
				>
					<Flex vertical gap={16}>
						{'Please verify the details match your bank statement before proceeding.'}
						<Descriptions layout='vertical' column={2} colon={false} className='allocation-overview-descriptions'>
							<Descriptions.Item label={'Remittance No.'}>{data?.metadata?.eraNumber || EMPTY_VALUE}</Descriptions.Item>
							<Descriptions.Item label={'Total Paid Amount'}>
								{formatAmount(fhirResource?.amount?.value) || EMPTY_VALUE}
							</Descriptions.Item>
						</Descriptions>
						<Flex vertical gap={8}>
							{commentLabel}
							<Input.TextArea
								placeholder={'Enter your comment here'}
								value={note}
								onChange={(e) => setNote(e.target.value)}
								data-testid='ipr-reconcile-note'
							/>
						</Flex>
					</Flex>
				</Modal>
			</>
		);
	}

	if (showReconciledTag) {
		return (
			<Tag color={ANT_TAG_COLORS.green} className='m-0' data-testid='ipr-reconciled-tag'>
				{'Reconciled'}
			</Tag>
		);
	}
	return null;
};

export { commentLabel };
export default ReconcilePayment;
